import { FC, useEffect, useState } from 'react'
import { InputNew } from '@UI/InputNew'

type CreateLinkNumberInputProps = {
  value?: number | string | null
  onChange?: (value: number | null) => void
  /** Только целые (период, скидка в рублях, кол-во). */
  integer?: boolean
  min?: number
  max?: number
  title?: string
  className?: string
  disabled?: boolean
  placeholder?: string | number
  inputMode?: string
  step?: number | string
  onFocus?: (event: unknown) => void
  onBlur?: (event: unknown) => void
}

const toDraft = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return ''
  return String(value)
}

const sanitize = (raw: string, integer: boolean): string => {
  if (integer) return raw.replace(/[^\d]/g, '')
  const cleaned = raw.replace(/[^\d.]/g, '')
  const dot = cleaned.indexOf('.')
  if (dot === -1) return cleaned
  return `${cleaned.slice(0, dot + 1)}${cleaned.slice(dot + 1).replace(/\./g, '')}`
}

/**
 * Числовой инпут для create-link: можно стереть последнюю цифру (пусто),
 * без мгновенного `Number('') || 1` и без native type=number + min.
 * Совместим с antd Form.Item (value / onChange).
 */
const CreateLinkNumberInput: FC<CreateLinkNumberInputProps> = ({
  value,
  onChange,
  integer = false,
  min,
  max,
  onFocus,
  onBlur,
  inputMode,
  ...rest
}) => {
  const [focused, setFocused] = useState(false)
  const [draft, setDraft] = useState(() => toDraft(value))

  useEffect(() => {
    if (!focused) setDraft(toDraft(value))
  }, [focused, value])

  const commit = (raw: string, clamp: boolean) => {
    if (raw === '' || raw === '.') {
      onChange?.(null)
      return
    }
    let num = Number(raw)
    if (!Number.isFinite(num)) return
    if (integer) num = Math.trunc(num)
    if (clamp) {
      if (min != null && num < min) num = min
      if (max != null && num > max) num = max
    }
    onChange?.(num)
    if (clamp) setDraft(String(num))
  }

  return (
    // @ts-ignore
    <InputNew
      {...rest}
      type="text"
      inputMode={inputMode ?? (integer ? 'numeric' : 'decimal')}
      value={focused ? draft : toDraft(value)}
      onFocus={(event: unknown) => {
        setFocused(true)
        setDraft(toDraft(value))
        onFocus?.(event)
      }}
      onBlur={(event: unknown) => {
        setFocused(false)
        // На blur пусто оставляем пустым (валидация формы покажет ошибку).
        // clamp только если есть число.
        if (draft !== '' && draft !== '.') {
          commit(draft, true)
        } else {
          onChange?.(null)
          setDraft('')
        }
        onBlur?.(event)
      }}
      onChange={(event: { target?: { value?: string } } | string) => {
        const raw = sanitize(
          String(
            typeof event === 'string' ? event : event?.target?.value ?? '',
          ),
          integer,
        )
        setDraft(raw)
        commit(raw, false)
      }}
    />
  )
}

export default CreateLinkNumberInput

/** Пустой/нечисловой ввод при редактировании — не подменять дефолтом. */
export const isEmptyNumberInputValue = (value: unknown): boolean =>
  value === null || value === undefined || value === ''

/** Парсинг числа из инпута; пусто → null (не 0/1). */
export const parseCreateLinkNumberInput = (
  value: unknown,
  options?: { integer?: boolean; min?: number; max?: number },
): number | null => {
  if (isEmptyNumberInputValue(value)) return null
  let num = Number(value)
  if (!Number.isFinite(num)) return null
  if (options?.integer) num = Math.trunc(num)
  if (options?.min != null && num < options.min) num = options.min
  if (options?.max != null && num > options.max) num = options.max
  return num
}

/**
 * Для antd Form rules: пусто не превращать в Number(null)===0,
 * иначе required/min пропускают «пустой» инпут.
 */
export const createLinkNumberRuleTransform = (value: unknown) =>
  isEmptyNumberInputValue(value) ? Number.NaN : Number(value)
